import * as ReactQuery from 'react-query';
import { InferMutationOptions, Services } from 'services';
import { Store } from 'store';
import { useIntl } from 'react-intl';
import { requestMedicalRecordsAPI } from './api';

const REQUEST_MEDICAL_RECORDS_KEY = '@referrals/request-medical-records';

export function useRequestMedicalRecords(
  options?: InferMutationOptions<typeof requestMedicalRecordsAPI>
) {
  const queryClient = ReactQuery.useQueryClient();
  const { showNotification } = Store.useNotifications();
  const intl = useIntl();

  const { mutate, isLoading } = ReactQuery.useMutation(
    REQUEST_MEDICAL_RECORDS_KEY,
    requestMedicalRecordsAPI,
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries([
          Services.Referrals.KEYS.GET_REFERRAL_KEY,
          variables.referralId,
        ]);
        queryClient.invalidateQueries([
          Services.Referrals.KEYS.GET_REFERRAL_ACTIVITY_KEY,
          variables.referralId,
        ]);
        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
      },
      onError: (error, variables, context) => {
        // 409 means the request already exists, so the cached referral and
        // activity log are out of date. Other failures write nothing.
        if (error.statusCode === 409) {
          queryClient.invalidateQueries([
            Services.Referrals.KEYS.GET_REFERRAL_KEY,
            variables.referralId,
          ]);
          queryClient.invalidateQueries([
            Services.Referrals.KEYS.GET_REFERRAL_ACTIVITY_KEY,
            variables.referralId,
          ]);
        }
        showNotification({
          variant: 'error',
          description:
            error.statusCode === 503
              ? intl.formatMessage({
                  id: 'is.request_medical_records.try_again',
                  defaultMessage: 'Request failed. Please try again.',
                })
              : error.message,
        });
        if (options?.onError) {
          options.onError(error, variables, context);
        }
      },
    }
  );

  return {
    requestMedicalRecords: mutate,
    isLoading,
  };
}
